import styled, { css } from 'styled-components';
import { GhostButton, LinkButton, SolidButton } from 'dbi-pearl-ui-kit';
import { PEARL_CONNECT_ACCENT } from 'vendor/styles/pearlConnectFormUiOverrides';

const PEARL_CONNECT_TEXT = '#132122';
const PEARL_CONNECT_MUTED = '#555';
const PEARL_CONNECT_SURFACE = '#f5f8f8';
const PEARL_CONNECT_BORDER = '#e8eeef';

/** Pearl Connect signup claim modal — opened from VendorBusinessDetailsFormNew */
const ClaimableWebstoresDialogStyled = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    color: ${PEARL_CONNECT_TEXT};
    gap: 16px;

    .dialog-heading {
        margin: 0;
        font-family: 'Objektiv Mk2 Bold', sans-serif;
        font-size: 20px;
        line-height: 28px;
        text-transform: lowercase;
    }

    .dialog-heading::first-letter {
        text-transform: uppercase;
    }

    .dialog-subtitle {
        margin: 0;
        font-size: 14px;
        line-height: 20px;
        color: ${PEARL_CONNECT_MUTED};
    }

    .dialog-selected-store {
        padding: 12px 14px;
        border-radius: 8px;
        border: 1px solid ${PEARL_CONNECT_BORDER};
        background: ${PEARL_CONNECT_SURFACE};
    }

    .dialog-selected-store-name {
        font-family: 'Objektiv Mk2 Bold', sans-serif;
        font-size: 15px;
        line-height: 22px;
    }

    .dialog-selected-store-meta {
        margin-top: 4px;
        font-size: 12px;
        line-height: 18px;
        color: ${PEARL_CONNECT_MUTED};
    }

    .dialog-verify-body {
        font-size: 14px;
        line-height: 20px;
    }

    .dialog-verify-email {
        font-family: 'Objektiv Mk2 Bold', sans-serif;
        color: ${PEARL_CONNECT_ACCENT};
    }

    .dialog-verify-subtitle {
        margin-top: 12px;
        font-size: 14px;
        line-height: 20px;
        color: ${PEARL_CONNECT_MUTED};
    }

    .dialog-success-step {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .dialog-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
        max-height: 50vh;
        overflow-y: auto;
        padding-right: 4px;
    }

    .dialog-card {
        display: flex;
        flex-direction: column;
        gap: 6px;
        padding: 14px 16px;
        border: 1px solid var(--BorderPrimary, #d1d1d1);
        border-radius: 6px;
        background: #fff;
    }

    .dialog-card-name {
        font-family: 'Objektiv Mk2 Bold', sans-serif;
        font-size: 15px;
        line-height: 22px;
    }

    .dialog-card-meta,
    .dialog-card-address,
    .dialog-card-categories {
        font-size: 12px;
        line-height: 18px;
        color: var(--TextSecondary, #555);
    }

    .dialog-card-address {
        margin-top: 2px;
        color: ${PEARL_CONNECT_TEXT};
    }

    .dialog-card-actions {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 8px;
        flex-wrap: wrap;
    }

    .dialog-footer {
        display: flex;
        justify-content: center;
        padding-top: 4px;
    }
`;

const DialogCodeField = styled.div`
    width: 100%;
    margin: 16px 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

const DialogContinueLink = styled.button`
    margin: 0;
    padding: 0 0 8px;
    border: none;
    border-bottom: 1px solid ${PEARL_CONNECT_ACCENT};
    background: transparent;
    cursor: pointer;
    font-family: 'Objektiv Mk2', sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: ${PEARL_CONNECT_ACCENT};
    text-align: center;

    &:hover,
    &:focus-visible {
        color: ${PEARL_CONNECT_ACCENT};
        border-bottom-width: 2px;
        background: transparent;
        outline: none;
    }
`;

/* ui-kit LinkButton draws underline on inner <label>, and hover uses theme primary pink */
const dialogTealLinkStyles = css`
    && {
        border-bottom: none !important;
        text-decoration: none !important;
        box-shadow: none !important;
        background-color: transparent !important;
        background-image: none !important;
    }

    && label {
        border-bottom: 1px solid ${PEARL_CONNECT_ACCENT} !important;
        padding-bottom: 8px;
    }

    && label b,
    && * {
        color: ${PEARL_CONNECT_ACCENT} !important;
    }

    &&:hover,
    &&:focus-visible {
        background-color: transparent !important;
    }

    &&:hover label,
    &&:focus-visible label {
        border-bottom: 2px solid ${PEARL_CONNECT_ACCENT} !important;
    }

    &&:hover label b,
    &&:hover *,
    &&:focus-visible label b,
    &&:focus-visible * {
        color: ${PEARL_CONNECT_ACCENT} !important;
    }
`;

const DialogBackLink = styled(LinkButton)`
    ${dialogTealLinkStyles}

    && {
        align-self: flex-start;
        padding: 0;
        min-width: 0;
        width: auto;
        font-size: 14px;
    }
`;

const DialogSendButton = styled(SolidButton)`
    && {
        align-self: flex-start;
        width: auto !important;
        max-width: 100%;
        margin-top: 16px;
        background-color: ${PEARL_CONNECT_ACCENT} !important;
        border-color: ${PEARL_CONNECT_ACCENT} !important;
        border-radius: 10px !important;
        padding: 10px 20px !important;
        min-height: 40px !important;
        font-size: 14px !important;
        white-space: nowrap;
    }

    &&:hover:not(:disabled),
    &&:focus-visible:not(:disabled) {
        background-color: #234a4c !important;
        border-color: #234a4c !important;
        color: #ffffff !important;
    }

    &&:hover:not(:disabled) *,
    &&:focus-visible:not(:disabled) * {
        color: #ffffff !important;
    }

    &&:disabled {
        opacity: 0.6;
    }
`;

const DialogClaimButton = styled(GhostButton)`
    && {
        min-width: 0;
        border-radius: 8px !important;
        color: ${PEARL_CONNECT_ACCENT} !important;
        border-color: ${PEARL_CONNECT_ACCENT} !important;
        transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
    }

    &&:hover,
    &&:focus-visible {
        background-color: ${PEARL_CONNECT_ACCENT} !important;
        border-color: ${PEARL_CONNECT_ACCENT} !important;
        color: #ffffff !important;
    }

    &&:hover *,
    &&:focus-visible * {
        color: #ffffff !important;
    }
`;

const DialogResendLink = styled(LinkButton)`
    ${dialogTealLinkStyles}

    && {
        padding: 0;
        min-width: 0;
        font-size: 14px;
        margin-top: 12px;
    }
`;

const DialogCompleteButton = styled(SolidButton)`
    && {
        align-self: flex-start;
        width: auto !important;
        margin-top: 16px;
        background-color: ${PEARL_CONNECT_ACCENT} !important;
        border-color: ${PEARL_CONNECT_ACCENT} !important;
        border-radius: 10px !important;
        padding: 10px 20px !important;
        min-height: 40px !important;
        font-size: 14px !important;
    }

    &&:disabled {
        opacity: 0.6;
    }
`;

export {
    ClaimableWebstoresDialogStyled,
    DialogBackLink,
    DialogSendButton,
    DialogContinueLink,
    DialogCodeField,
    DialogClaimButton,
    DialogResendLink,
    DialogCompleteButton,
};
